<script setup lang="ts">
const { t, locale } = useI18n()
const route = useRoute()
const { fetchPage } = useStaticPage()
const runtimeConfig = useRuntimeConfig()

const slug = computed(() => route.params.slug as string)

// SSR fetch so Googlebot receives the real content (not a client-only skeleton).
// A missing page surfaces as `error` (apiGet throws on non-2xx) — we convert it
// into a hard 404 so it stops being reported as a soft 404 / thin page.
const { data: page, error } = await useAsyncData(
  () => `static-page-${slug.value}`,
  () => fetchPage(slug.value),
  { watch: [slug] },
)

if (error.value || !page.value) {
  throw createError({ statusCode: 404, statusMessage: 'Page not found', fatal: true })
}

// Canonical + hreflang (same slug across locales; backend resolves copy by locale).
useSeoCanonical(() => `/p/${slug.value}`)

useSeoMeta({
  title: () => `${page.value?.title} - MiDiGi`,
  ogTitle: () => page.value?.title,
  description: () => page.value?.metaDescription || '',
  ogDescription: () => page.value?.metaDescription || '',
})

function formatDate(date: string) {
  if (!date) return ''
  return new Date(date).toLocaleDateString()
}

function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;')
}

const renderedContent = computed(() => {
  let content = page.value?.content || ''

  // The original terms template expected legal-name and tax-ID placeholders.
  // This deployment publishes only the customer contact details supplied by
  // the operator, so rewrite those five localized introductions as brand copy.
  const identityPhrases: Record<string, string> = {
    'titularidad de [RAZÓN SOCIAL], con NIF [NIF/CIF] y domicilio en [DOMICILIO]': 'gestionado bajo la marca MiDiGi, con domicilio de contacto en [DOMICILIO]',
    'owned by [RAZÓN SOCIAL], with tax ID [NIF/CIF] and registered office at [DOMICILIO]': 'operated under the MiDiGi brand, with contact address at [DOMICILIO]',
    'propriété de [RAZÓN SOCIAL], titulaire du numéro fiscal [NIF/CIF] et dont le siège est situé à [DOMICILIO]': 'exploité sous la marque MiDiGi, avec adresse de contact à [DOMICILIO]',
    'propriedade de [RAZÓN SOCIAL], com número fiscal [NIF/CIF] e sede em [DOMICILIO]': 'operado sob a marca MiDiGi, com endereço de contacto em [DOMICILIO]',
    '本网站由 [RAZÓN SOCIAL] 所有,税号 [NIF/CIF],注册地址 [DOMICILIO]': '本网站以 MiDiGi 品牌运营，联系地址为 [DOMICILIO]',
  }
  for (const [source, replacement] of Object.entries(identityPhrases)) {
    content = content.replaceAll(source, replacement)
  }

  const updatedDate = page.value?.updatedAt?.slice(0, 10) || ''
  const tokens: Record<string, string> = {
    '[RAZÓN SOCIAL]': 'MiDiGi',
    '[NIF/CIF]': '',
    '[DOMICILIO]': runtimeConfig.public.businessRegisteredAddress as string,
    '[EMAIL]': runtimeConfig.public.businessEmail as string,
    '[FECHA]': updatedDate,
  }

  for (const [token, value] of Object.entries(tokens)) {
    content = content.replaceAll(token, escapeHtml(value))
  }

  // DB-authored HTML does not pass through NuxtLink/localePath. Keep internal
  // legal-page navigation in the visitor's current language.
  if (locale.value !== 'es') {
    content = content.replaceAll('href="/p/', `href="/${locale.value}/p/`)
  }

  // Use the configured public contact rather than stale addresses that may
  // survive in database-authored About and legal pages.
  const email = runtimeConfig.public.businessEmail as string
  if (email) {
    content = content.replaceAll('servicio@midigi.net', escapeHtml(email))
    content = content.replace(/href="mailto:[^"]+"/g, `href="mailto:${escapeHtml(email)}"`)
  }

  return content
})
</script>

<template>
  <div class="min-h-screen bg-white dark:bg-gray-950">
    <div class="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8">
      <h1 class="text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl mb-4">
        {{ page!.title }}
      </h1>

      <p v-if="page!.updatedAt" class="text-sm text-gray-500 dark:text-gray-400 mb-8">
        {{ t('staticPage.lastUpdated') }}: {{ formatDate(page!.updatedAt) }}
      </p>

      <div
        class="prose prose-gray dark:prose-invert max-w-none"
        v-html="sanitizeHtml(renderedContent)"
      />
    </div>
  </div>
</template>
